<template>
  <div class="extras-tab">
    <div class="mb-3">
      <label class="form-label">Custom CSS Class</label>
      <input
        type="text"
        class="form-control"
        placeholder="e.g., my-custom-class"
        :value="component.extras.customClass"
        @input="updateExtras('customClass', ($event.target as HTMLInputElement).value)"
      />
      <small class="form-text text-muted">Add custom CSS classes to this component</small>
    </div>

    <h6 class="mb-3">Validation (for input fields)</h6>

    <div class="mb-3">
      <label class="form-label">Pattern (Regular Expression)</label>
      <input
        type="text"
        class="form-control"
        placeholder="e.g., ^[A-Za-z]+$"
        :value="component.extras.validation?.pattern"
        @input="updateValidation('pattern', ($event.target as HTMLInputElement).value)"
      />
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <label class="form-label">Min Length</label>
        <input
          type="number"
          class="form-control"
          :value="component.extras.validation?.minLength"
          @input="updateValidation('minLength', parseInt(($event.target as HTMLInputElement).value))"
        />
      </div>

      <div class="col-md-6 mb-3">
        <label class="form-label">Max Length</label>
        <input
          type="number"
          class="form-control"
          :value="component.extras.validation?.maxLength"
          @input="updateValidation('maxLength', parseInt(($event.target as HTMLInputElement).value))"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <label class="form-label">Min Value</label>
        <input
          type="number"
          class="form-control"
          :value="component.extras.validation?.min"
          @input="updateValidation('min', parseInt(($event.target as HTMLInputElement).value))"
        />
      </div>

      <div class="col-md-6 mb-3">
        <label class="form-label">Max Value</label>
        <input
          type="number"
          class="form-control"
          :value="component.extras.validation?.max"
          @input="updateValidation('max', parseInt(($event.target as HTMLInputElement).value))"
        />
      </div>
    </div>

    <h6 class="mb-3 mt-4">Custom Attributes</h6>
    <div class="mb-2">
      <small class="text-muted">Add custom HTML attributes to this component</small>
    </div>

    <div v-for="(value, key) in component.extras.customAttributes" :key="key" class="input-group mb-2">
      <input
        type="text"
        class="form-control"
        placeholder="Attribute name"
        :value="key"
        disabled
      />
      <input
        type="text"
        class="form-control"
        placeholder="Attribute value"
        :value="value"
        @input="updateCustomAttribute(key, ($event.target as HTMLInputElement).value)"
      />
      <button class="btn btn-outline-danger" type="button" @click="removeCustomAttribute(key)">
        ×
      </button>
    </div>

    <div class="input-group mb-3">
      <input
        type="text"
        class="form-control"
        placeholder="New attribute name"
        v-model="newAttributeName"
      />
      <input
        type="text"
        class="form-control"
        placeholder="New attribute value"
        v-model="newAttributeValue"
      />
      <button class="btn btn-outline-primary" type="button" @click="addCustomAttribute">
        Add
      </button>
    </div>

    <h6 class="mb-3 mt-4">Custom Data</h6>
    <div class="mb-2">
      <small class="text-muted">Store custom data with this component (JSON format)</small>
    </div>

    <textarea
      class="form-control font-monospace"
      rows="5"
      :value="customDataString"
      @input="updateCustomData(($event.target as HTMLTextAreaElement).value)"
    ></textarea>
    <small v-if="customDataError" class="text-danger">{{ customDataError }}</small>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { FormComponent, ExtrasProperties } from '../../types/form-builder';

interface Props {
  component: FormComponent;
}

interface Emits {
  (e: 'update', updates: Partial<FormComponent>): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const newAttributeName = ref('');
const newAttributeValue = ref('');
const customDataError = ref('');

const customDataString = computed(() => {
  if (!props.component.extras.customData) return '';
  return JSON.stringify(props.component.extras.customData, null, 2);
});

function updateExtras(field: keyof ExtrasProperties, value: any) {
  emit('update', {
    extras: {
      ...props.component.extras,
      [field]: value || undefined,
    },
  });
}

function updateValidation(field: string, value: any) {
  const validation = {
    ...props.component.extras.validation,
    [field]: value || undefined,
  };

  emit('update', {
    extras: {
      ...props.component.extras,
      validation,
    },
  });
}

function updateCustomAttribute(key: string, value: string) {
  const customAttributes = {
    ...props.component.extras.customAttributes,
    [key]: value,
  };

  emit('update', {
    extras: {
      ...props.component.extras,
      customAttributes,
    },
  });
}

function removeCustomAttribute(key: string) {
  const customAttributes = { ...props.component.extras.customAttributes };
  delete customAttributes[key];

  emit('update', {
    extras: {
      ...props.component.extras,
      customAttributes,
    },
  });
}

function addCustomAttribute() {
  if (!newAttributeName.value) return;

  const customAttributes = {
    ...props.component.extras.customAttributes,
    [newAttributeName.value]: newAttributeValue.value,
  };

  emit('update', {
    extras: {
      ...props.component.extras,
      customAttributes,
    },
  });

  newAttributeName.value = '';
  newAttributeValue.value = '';
}

function updateCustomData(value: string) {
  try {
    const parsed = value ? JSON.parse(value) : {};
    customDataError.value = '';

    emit('update', {
      extras: {
        ...props.component.extras,
        customData: parsed,
      },
    });
  } catch (error) {
    customDataError.value = 'Invalid JSON format';
  }
}
</script>
